/**
 * Header Organism
 *
 * The main header component for public pages.
 * Contains logo, cart, user info, language selector, and optional stepper.
 */

'use client';

import { useTranslation } from '@/app/i18n/client';
import { Button } from '@/components/atoms/buttons';
import { Checkbox } from '@/components/atoms/inputs';
import MaspLogo from '@/components/icons/maspLogo';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/molecules/feedback';
import { LanguageSelector, Stepper } from '@/components/molecules/navigation';
import { useConvenienceFee } from '@/context/ConvenienceFeeProvider';
import useLocation from '@/context/LocationProvider';
import { useAuthV2 } from '@/hooks/useAuthV2';
import useCart from '@/hooks/useCart';
import useGlobalState from '@/hooks/useGlobalState';
import { useIsMobileResolution } from '@/hooks/useIsMobileResolution';
import { getNameLocations } from '@/utils/locations';
import { UserGroups } from '@/utils/types';
import { ArrowLeft, Loader2, MapPin } from 'lucide-react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { twMerge } from 'tailwind-merge';

interface HeaderProps {
  dark?: boolean;
  showStepper?: boolean;
}

export const Header = ({ dark = false, showStepper = false }: HeaderProps) => {
  const { lng } = useParams() as { lng: string };
  const { t } = useTranslation(lng, 'header');
  const { state } = useGlobalState();
  const { cartTicketsNumber } = useCart();
  const { user, userData, userGroups } = useAuthV2();
  const hasItemsOnCart = cartTicketsNumber > 0;
  const { isMobile } = useIsMobileResolution();
  const isAdmin = userGroups.includes(UserGroups.ADMIN) || userGroups.includes(UserGroups.STAFF);

  const { locationModal, setIsOpenModalSetLocation } = useLocation();
  const { convenienceFee, handleConvenienceFee, isLoading } = useConvenienceFee();

  return (
    <>
      <header
        id='main-nav'
        data-testid='header'
        role='banner'
        className='h-[75px] w-full bg-background max-sm:bg-transparent'>
        <div
          className={twMerge(
            'fixed inset-x-0 top-0 z-50 flex h-[75px] w-full items-center justify-between bg-background p-0 max-sm:pt-[15px]',
            'left-1/2 mx-auto max-w-[calc(100%-40px)] -translate-x-1/2 max-md:max-w-[calc(100%-20px)]'
          )}
          style={{
            borderColor: 'hsl(var(--border))',
          }}>
          <div className='flex w-full items-center justify-between border-b border-border py-[10px] max-sm:border-t'>
            <Link
              href={process.env.NEXT_APP_ENVIRONMENT === 'production' ? 'https://qrch.net/sqr2m7npou56' : '/'}
              data-testid='header-logo'
              aria-label='MASP - Página inicial'>
              <MaspLogo size={isMobile ? 30 : 40} />
            </Link>
            <div className='flex items-center gap-6'>
              {isAdmin && (
                <>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Link href={`/${lng}/admin/experiences`}>
                          <Button
                            variant='outline'
                            className='relative transition-[2000ms] hover:-translate-x-2 max-sm:-right-4'>
                            <ArrowLeft size={20} />
                          </Button>
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent>{t('backToAdminTooltip')}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <div className='flex w-fit items-center gap-2'>
                    {isLoading ? (
                      <span className='flex w-44 items-center justify-center text-center'>
                        <Loader2 className='h-4 w-4 animate-spin' />
                      </span>
                    ) : (
                      <>
                        <Checkbox checked={convenienceFee} onCheckedChange={handleConvenienceFee} />
                        <p className='text-lg font-medium'>Taxa de conveniência</p>
                      </>
                    )}
                  </div>
                </>
              )}
              <Link href='/cart' data-testid='header-cart-link'>
                <div className='relative cursor-pointer'>
                  {t('cart')}
                  {hasItemsOnCart && (
                    <div className='absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-sm font-semibold text-slate-50'>
                      {cartTicketsNumber}
                    </div>
                  )}
                </div>
              </Link>

              {isAdmin && (
                <>
                  <div className='h-10 w-px bg-border' />
                  <div
                    className='flex cursor-pointer items-center gap-2'
                    onClick={() => setIsOpenModalSetLocation(true)}>
                    <MapPin className='text-primary-700' />
                    {locationModal ? (
                      <p className='w-full text-lg text-primary-700'>{getNameLocations(locationModal)}</p>
                    ) : (
                      <p className='text-lg text-primary-700'>Local</p>
                    )}
                  </div>
                </>
              )}

              <div className='h-10 w-px bg-border' />

              <Link
                href={user ? (isAdmin ? `/${lng}/admin/experiences` : `/${lng}/my-account`) : `/${lng}/login`}
                data-testid='header-user-link'>
                <div className='flex cursor-pointer items-center'>
                  {user ? (
                    <>
                      {userData && userData.name ? (
                        <span className={`ml-2 max-w-24 truncate text-foreground md:max-w-48`}>
                          {t('hello', { name: userData.name.split(' ')[0] })}
                        </span>
                      ) : (
                        <span className={`ml-2 max-w-24 truncate text-foreground md:max-w-48`}>
                          {t('hello', { name: 'Visitante' })}
                        </span>
                      )}
                    </>
                  ) : userGroups.includes(UserGroups.ADMIN) || userGroups.includes(UserGroups.STAFF) ? (
                    <></>
                  ) : (
                    'Login'
                  )}
                </div>
              </Link>
              <LanguageSelector />
            </div>
          </div>
          {showStepper && <Stepper currentStep={state.currentStep ?? 0} totalSteps={state.totalSteps ?? 5} />}
        </div>
      </header>
    </>
  );
};

export default Header;
